import type { AnyLevel } from '../data/schemas';

/** Age bands a parent can pick. Kept small and coarse on purpose. */
export const AGE_BANDS = ['2-3', '3-4', '5-6'] as const;
export type AgeBand = (typeof AGE_BANDS)[number];

export interface AgeSettings {
  /** Highest level chapter shown on the level list. */
  maxChapter: number;
  /** Seconds without a tap before the spoken hint + pointing hand. */
  idleHintSec: number;
  /** Levels per play session before the truck "goes to sleep in the garage". */
  levelsPerSession: number;
}

export const AGE_SETTINGS: Record<AgeBand, AgeSettings> = {
  // 2–3: only the free-tap chapter, help comes quickly, short sessions.
  '2-3': { maxChapter: 1, idleHintSec: 3, levelsPerSession: 3 },
  // 3–4: adds colour sorting (chapter 2).
  '3-4': { maxChapter: 2, idleHintSec: 4, levelsPerSession: 4 },
  // 5–6: everything, more time to think before a hint.
  '5-6': { maxChapter: 3, idleHintSec: 6, levelsPerSession: 5 },
};

/** Parent-facing copy for the age picker (Norwegian). */
export const AGE_BAND_COPY: Record<AgeBand, { title: string; emoji: string; blurb: string }> = {
  '2-3': { title: '2–3 år', emoji: '🐣', blurb: 'Bare trykk – bilen gjør resten.' },
  '3-4': { title: '3–4 år', emoji: '🧸', blurb: 'Farger og enkle oppgaver.' },
  '5-6': { title: '5–6 år', emoji: '🚀', blurb: 'Sortering, gjenbruk og vær.' },
};

export function isAgeBand(value: unknown): value is AgeBand {
  return typeof value === 'string' && (AGE_BANDS as readonly string[]).includes(value);
}

export function settingsFor(band: AgeBand | null | undefined): AgeSettings {
  return AGE_SETTINGS[band ?? '5-6'];
}

/** Levels shown on the level list for this age band (by chapter). */
export function levelsForAge<T extends Pick<AnyLevel, 'chapter'>>(
  levels: T[],
  band: AgeBand | null | undefined
): T[] {
  const { maxChapter } = settingsFor(band);
  return levels.filter((level) => level.chapter <= maxChapter);
}
